import React, { useState } from "react";
import AuthGuard from "./components/AuthGuard";
import { SVG_ICONS } from "./components/TablerIconPicker";
import { ToastProvider } from "./components/Toast";
import LessonCreateTab from "./components/LessonCreateTab";
import LessonListTab from "./components/LessonListTab";
import PromptCreateTab from "./components/PromptCreateTab";
import PromptListTab from "./components/PromptListTab";
import ResourceCreateTab from "./components/ResourceCreateTab";
import ResourceListTab from "./components/ResourceListTab";
import NewsCreateTab from "./components/NewsCreateTab";
import NewsListTab from "./components/NewsListTab";
import AssessmentCreateTab from "./components/AssessmentCreateTab";
import AssessmentListTab from "./components/AssessmentListTab";
import EventCreateTab from "./components/EventCreateTab";
import EventListTab from "./components/EventListTab";
import ContentPlanningTab from "./components/ContentPlanningTab";
import SettingsTab from "./components/SettingsTab";
import NotificationsTab from "./components/NotificationsTab";
import UsersTab from "./components/UsersTab";
import PublicationTab from "./components/PublicationTab";
import ReportsTab from "./components/ReportsTab";
import DashboardTab from "./components/DashboardTab";
import { clearAuth } from "./utils/auth";
import { exportAllContent } from "./utils/contentExporter";
import "./styles/global.css";
import "./App.css";

function App() {
  const [activeMainSection, setActiveMainSection] = useState("dashboard"); // dashboard, content, settings, notifications, users, publication, reports
  const [activeContentSection, setActiveContentSection] = useState("lessons"); // lessons, prompts, resources, news, assessments, events
  const [showCreateModal, setShowCreateModal] = useState(false); // for create modal
  const [showCreateNotificationModal, setShowCreateNotificationModal] = useState(false); // for notification create modal
  const [refreshLoading, setRefreshLoading] = useState(false); // for refresh button loading state
  const [exportLoading, setExportLoading] = useState(false); // for export button loading state
  const [headerInfo, setHeaderInfo] = useState({ title: "Lessons", count: 0 });
  const lessonListRef = React.useRef(null);
  const promptListRef = React.useRef(null);
  const resourceListRef = React.useRef(null);
  const newsListRef = React.useRef(null);
  const assessmentListRef = React.useRef(null);
  const eventListRef = React.useRef(null);
  const contentPlanningRef = React.useRef(null);
  const reportsTabRef = React.useRef(null);
  const dashboardTabRef = React.useRef(null);
  const usersTabRef = React.useRef(null);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      await exportAllContent();
      alert("Content exported successfully!");
    } catch (err) {
      console.error("Export error:", err);
      alert("Failed to export content");
    } finally {
      setExportLoading(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      clearAuth();
      window.location.reload();
    }
  };

  const handleRefresh = async () => {
    if (!isRefreshEnabled || refreshLoading) return;

    setRefreshLoading(true);
    try {
      if (activeMainSection === "dashboard" && dashboardTabRef.current) {
        await dashboardTabRef.current.refreshData();
      } else if (activeMainSection === "reports" && reportsTabRef.current) {
        await reportsTabRef.current.refreshData();
      } else if (activeMainSection === "users" && usersTabRef.current) {
        await usersTabRef.current.refreshData();
      }
    } finally {
      setRefreshLoading(false);
    }
  };

  const isRefreshEnabled = ["dashboard", "reports", "users"].includes(activeMainSection);

  React.useEffect(() => {
    if (activeMainSection === "content") {
      const titles = { lessons: "Lessons", prompts: "Prompts", resources: "Resources", news: "News", assessments: "Assessments", events: "Events" };
      // Reset to 0 immediately on tab switch; each list reports its real count once its data finishes loading.
      setHeaderInfo({ title: titles[activeContentSection] || "Content", count: 0 });
    }
  }, [activeContentSection, activeMainSection]);

  const contentSections = [
    { id: "lessons", label: "Lessons", icon: "book" },
    { id: "prompts", label: "Prompts", icon: "message-square" },
    { id: "resources", label: "Resources", icon: "link" },
    { id: "news", label: "News", icon: "rss" },
    { id: "assessments", label: "Assessments", icon: "clipboard-list" },
    { id: "events", label: "Events", icon: "calendar" },
    { id: "planning", label: "Content Planning", icon: "checklist" },
  ];

  return (
    <ToastProvider>
      <AuthGuard>
        {/* Toast notifications enabled for all save/update/delete actions */}
        <div className="App">
        <header className="app-header">
          <img src="/plane.png" alt="Practical AI" className="header-logo" />
          <h1>ADMIN PORTAL - LEAD AI</h1>
          <span className="header-version">v2.1.35</span>
          <button disabled={exportLoading} onClick={handleExport} className="export-btn" style={{display: "flex", alignItems: "center", gap: "8px", background: "transparent", border: `1px solid ${!exportLoading ? "rgba(34, 197, 94, 0.3)" : "rgba(122, 156, 196, 0.2)"}`, borderRadius: "6px", color: !exportLoading ? "#22C55E" : "#7A9CC4", cursor: !exportLoading ? "pointer" : "not-allowed", padding: "8px 12px", fontSize: "14px", fontWeight: "600", transition: "all 0.2s", marginRight: "12px", opacity: !exportLoading ? 1 : 0.5}} onMouseEnter={(e) => {if (!exportLoading) { e.target.style.background = "rgba(34, 197, 94, 0.1)"; e.target.style.borderColor = "rgba(34, 197, 94, 0.5)"; }}} onMouseLeave={(e) => {if (!exportLoading) { e.target.style.background = "transparent"; e.target.style.borderColor = "rgba(34, 197, 94, 0.3)"; }}}>{SVG_ICONS["download"]({size: 16, color: !exportLoading ? "#22C55E" : "#7A9CC4"})} {exportLoading ? "Exporting..." : "Export HTML"}</button>
          <button disabled={!isRefreshEnabled || refreshLoading} onClick={handleRefresh} className="refresh-btn" style={{display: "flex", alignItems: "center", gap: "8px", background: "transparent", border: `1px solid ${isRefreshEnabled && !refreshLoading ? "rgba(59, 130, 246, 0.3)" : "rgba(122, 156, 196, 0.2)"}`, borderRadius: "6px", color: isRefreshEnabled && !refreshLoading ? "#5B9EF0" : "#7A9CC4", cursor: isRefreshEnabled && !refreshLoading ? "pointer" : "not-allowed", padding: "8px 12px", fontSize: "14px", fontWeight: "600", transition: "all 0.2s", marginRight: "12px", opacity: isRefreshEnabled && !refreshLoading ? 1 : 0.5}} onMouseEnter={(e) => {if (isRefreshEnabled && !refreshLoading) { e.target.style.background = "rgba(59, 130, 246, 0.1)"; e.target.style.borderColor = "rgba(59, 130, 246, 0.5)"; }}} onMouseLeave={(e) => {if (isRefreshEnabled && !refreshLoading) { e.target.style.background = "transparent"; e.target.style.borderColor = "rgba(59, 130, 246, 0.3)"; }}}>{refreshLoading ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{animation: "spin 1s linear infinite"}}><circle cx="12" cy="12" r="10"></circle><path d="M12 2a10 10 0 0 1 10 10"></path></svg> : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" /></svg>} {refreshLoading ? "Refreshing..." : "Refresh"}</button>
          <button onClick={handleLogout} className="logout-btn" style={{display: "flex", alignItems: "center", gap: "8px"}}>{SVG_ICONS["arrow-left"]({size: 16, color: "#F87171"})} Logout</button>
        </header>

        {/* Top-Level Navigation Menu */}
        <nav className="main-nav">
          <button
            className={`nav-button ${activeMainSection === "dashboard" ? "active" : ""}`}
            onClick={() => setActiveMainSection("dashboard")}
          >
            Dashboard
          </button>
          <button
            className={`nav-button ${activeMainSection === "users" ? "active" : ""}`}
            onClick={() => setActiveMainSection("users")}
          >
            Users
          </button>
          <button
            className={`nav-button ${activeMainSection === "content" ? "active" : ""}`}
            onClick={() => setActiveMainSection("content")}
          >
            Content
          </button>
          <button
            className={`nav-button ${activeMainSection === "publication" ? "active" : ""}`}
            onClick={() => setActiveMainSection("publication")}
          >
            Publication List
          </button>
          <button
            className={`nav-button ${activeMainSection === "reports" ? "active" : ""}`}
            onClick={() => setActiveMainSection("reports")}
          >
            Reports
          </button>
          <button
            className={`nav-button ${activeMainSection === "notifications" ? "active" : ""}`}
            onClick={() => setActiveMainSection("notifications")}
          >
            Notifications
          </button>
          <button
            className={`nav-button ${activeMainSection === "settings" ? "active" : ""}`}
            onClick={() => setActiveMainSection("settings")}
          >
            Settings
          </button>
        </nav>

        <div className="app-layout">
          {/* Left Sidebar - Only shown for Content */}
          {activeMainSection === "content" && (
            <aside className="content-sidebar">
              {contentSections.map((section) => {
                const IconComponent = SVG_ICONS[section.icon];
                const isActive = activeContentSection === section.id;
                return (
                <button
                  key={section.id}
                  className="content-sidebar-btn"
                  onClick={() => {
                    setActiveContentSection(section.id);
                  }}
                  style={{
                    padding: "10px 12px",
                    background:
                      isActive
                        ? "rgba(59, 130, 246, 0.2)"
                        : "transparent",
                    border:
                      isActive
                        ? "1px solid rgba(59, 130, 246, 0.4)"
                        : "1px solid transparent",
                    color:
                      isActive
                        ? "#5B9EF0"
                        : "#7A9CC4",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "500",
                    textAlign: "left",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {IconComponent && <IconComponent size={16} color={isActive ? "#5B9EF0" : "#7A9CC4"} />}
                  {section.label}
                </button>
                );
              })}
            </aside>
          )}

          <main className={`app-main${activeMainSection === "content" ? " with-sidebar" : ""}`}>
            {/* Create Button Header - Only shown for Content */}
            {activeMainSection === "content" && activeContentSection !== "planning" && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                <h2 style={{ margin: 0, color: "#5B9EF0" }}>
                  {headerInfo.title} ({headerInfo.count})
                </h2>
                {activeContentSection !== "planning" && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    style={{
                      padding: "10px 16px",
                      background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
                      border: "none",
                      color: "white",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => (e.target.style.opacity = "0.9")}
                    onMouseLeave={(e) => (e.target.style.opacity = "1")}
                  >
                    ➕ Create New
                  </button>
                )}
              </div>
            )}

            {/* Create Modal - Overlay backdrop */}
            {showCreateModal && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(0, 0, 0, 0.7)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                }}
                onClick={() => setShowCreateModal(false)}
              >
                <div
                  style={{
                    background: "#050e1a",
                    borderRadius: "8px",
                    width: "90%",
                    maxWidth: "900px",
                    maxHeight: "90vh",
                    overflowY: "auto",
                    border: "1px solid rgba(59, 130, 246, 0.2)",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px", borderBottom: "1px solid rgba(59, 130, 246, 0.2)" }}>
                    <h2 style={{ margin: 0, color: "#5B9EF0" }}>Create New {activeContentSection.slice(0, -1)}</h2>
                    <button
                      onClick={() => setShowCreateModal(false)}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#7A9CC4",
                        fontSize: "24px",
                        cursor: "pointer",
                      }}
                    >
                      ✕
                    </button>
                  </div>
                  <div style={{ padding: "20px" }}>
                    {activeContentSection === "lessons" && <LessonCreateTab onCreated={() => { setShowCreateModal(false); lessonListRef.current?.loadLessons(); }} />}
                    {activeContentSection === "prompts" && <PromptCreateTab onCreated={() => { setShowCreateModal(false); promptListRef.current?.loadPrompts(); }} />}
                    {activeContentSection === "resources" && <ResourceCreateTab onCreated={() => { setShowCreateModal(false); resourceListRef.current?.loadResources(); }} />}
                    {activeContentSection === "news" && <NewsCreateTab onNewsCreated={() => { setShowCreateModal(false); newsListRef.current?.loadNews(); }} />}
                    {activeContentSection === "assessments" && <AssessmentCreateTab onCreated={() => { setShowCreateModal(false); assessmentListRef.current?.loadAssessments(); }} />}
                    {activeContentSection === "events" && <EventCreateTab onEventCreated={() => { setShowCreateModal(false); eventListRef.current?.loadEvents(); }} />}
                  </div>
                </div>
              </div>
            )}

            {/* Content List Sections - Always shown for Content */}
            {activeMainSection === "content" && (
              <>
                {activeContentSection === "lessons" && <LessonListTab ref={lessonListRef} onHeaderInfoChange={setHeaderInfo} />}
                {activeContentSection === "prompts" && <PromptListTab ref={promptListRef} onHeaderInfoChange={setHeaderInfo} />}
                {activeContentSection === "resources" && <ResourceListTab ref={resourceListRef} onHeaderInfoChange={setHeaderInfo} />}
                {activeContentSection === "news" && <NewsListTab ref={newsListRef} onHeaderInfoChange={setHeaderInfo} />}
                {activeContentSection === "assessments" && <AssessmentListTab ref={assessmentListRef} onHeaderInfoChange={setHeaderInfo} />}
                {activeContentSection === "events" && <EventListTab ref={eventListRef} onHeaderInfoChange={setHeaderInfo} />}
                {activeContentSection === "planning" && <ContentPlanningTab ref={contentPlanningRef} />}
              </>
            )}

            {/* Dashboard Section */}
            {activeMainSection === "dashboard" && <DashboardTab ref={dashboardTabRef} />}

            {/* Settings Section */}
            {activeMainSection === "settings" && <SettingsTab />}

            {/* Notifications Section */}
            {activeMainSection === "notifications" && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                  <h2 style={{ margin: 0, color: "#5B9EF0" }}>Notifications</h2>
                  <button
                    onClick={() => setShowCreateNotificationModal(true)}
                    style={{
                      padding: "10px 16px",
                      background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
                      border: "none",
                      color: "white",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => (e.target.style.opacity = "0.9")}
                    onMouseLeave={(e) => (e.target.style.opacity = "1")}
                  >
                    ➕ Create New
                  </button>
                </div>

                {/* Create Notification Modal */}
                {showCreateNotificationModal && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "rgba(0, 0, 0, 0.7)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1000,
                    }}
                    onClick={() => setShowCreateNotificationModal(false)}
                  >
                    <div
                      style={{
                        background: "#050e1a",
                        borderRadius: "8px",
                        width: "90%",
                        maxWidth: "700px",
                        maxHeight: "90vh",
                        overflowY: "auto",
                        border: "1px solid rgba(59, 130, 246, 0.2)",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px", borderBottom: "1px solid rgba(59, 130, 246, 0.2)" }}>
                        <h2 style={{ margin: 0, color: "#5B9EF0" }}>Create New Notification</h2>
                        <button
                          onClick={() => setShowCreateNotificationModal(false)}
                          style={{
                            background: "transparent",
                            border: "none",
                            color: "#7A9CC4",
                            fontSize: "24px",
                            cursor: "pointer",
                          }}
                        >
                          ✕
                        </button>
                      </div>
                      <div style={{ padding: "20px" }}>
                        <NotificationsTab isCreateMode onClose={() => setShowCreateNotificationModal(false)} />
                      </div>
                    </div>
                  </div>
                )}

                <NotificationsTab isViewMode />
              </>
            )}

            {/* Users Section */}
            {activeMainSection === "users" && <UsersTab ref={usersTabRef} />}

            {/* Publication Section */}
            {activeMainSection === "publication" && <PublicationTab ref={reportsTabRef} />}

            {/* Reports Section */}
            {activeMainSection === "reports" && <ReportsTab ref={reportsTabRef} />}
          </main>
        </div>
        </div>
      </AuthGuard>
    </ToastProvider>
  );
}

export default App;
